import { isEqual } from "../equality";
import { values } from "../objects";
import angle, { AngleUnits } from "./angle";
import area, { AreaUnits } from "convert-units/definitions/area";
import configMeasurements from "convert-units";
import coverage, { CoverageUnits } from "./coverage";
import current, { CurrentUnits } from "convert-units/definitions/current";
import density, { DensityUnits } from "./density";
import flow, { FlowUnits } from "./flow";
import length, { LengthUnits } from "convert-units/definitions/length";
import power, { PowerUnits } from "convert-units/definitions/power";
import rate, { RateUnits } from "./rate";
import speed, { SpeedUnits } from "convert-units/definitions/speed";
import time, { TimeUnits } from "convert-units/definitions/time";

export type ConvertUnits =
  | AreaUnits
  | AngleUnits
  | CoverageUnits
  | CurrentUnits
  | DensityUnits
  | FlowUnits
  | LengthUnits
  | PowerUnits
  | RateUnits
  | SpeedUnits
  | TimeUnits;

export const convert = configMeasurements<
  | "angle"
  | "area"
  | "coverage"
  | "current"
  | "density"
  | "flow"
  | "length"
  | "power"
  | "rate"
  | "speed"
  | "time",
  MeasurementSystem,
  ConvertUnits
>({
  angle,
  area,
  coverage,
  current,
  density,
  flow,
  length,
  power,
  rate,
  speed,
  time,
});

/**
 * CarbonMeasurementSystem is a system of units that we provide first-class
 * support for.  it should be used for most use cases involving, e.g., choosing
 * an appropriate unit based on user settings.
 */
export const CarbonMeasurementSystem = {
  imperial: "imperial",
  metric: "metric",
} as const;

export type CarbonMeasurementSystem =
  (typeof CarbonMeasurementSystem)[keyof typeof CarbonMeasurementSystem];

/**
 * MeasurementSystem also includes systems of units that `convert-units` says
 * a unit we handle belongs to; in our case, mostly measures of time, angle, and
 * electrical power.  it should be used for applications that specifically
 * make decisions based on unit system information provided by `convert-units`.
 */
export const MeasurementSystem = {
  ...CarbonMeasurementSystem,
  si: "SI",
} as const;

export type MeasurementSystem =
  (typeof MeasurementSystem)[keyof typeof MeasurementSystem];

export enum UnitNumberType {
  PERCENT = "%",
  COUNT = "_count",
}

export const isUnitNumberType = (units: CarbonUnit): units is UnitNumberType =>
  values(UnitNumberType).includes(units as UnitNumberType);

export type CarbonUnit = ConvertUnits | UnitNumberType;

export const carbonConvert = (
  value: number,
  fromUnits: CarbonUnit,
  toUnits: CarbonUnit
): number => {
  if (
    fromUnits === toUnits ||
    isEqual(fromUnits, toUnits) ||
    isUnitNumberType(fromUnits) ||
    isUnitNumberType(toUnits)
  ) {
    return value;
  }
  return convert(value).from(fromUnits).to(toUnits);
};

export type UnitSelector = { [system in CarbonMeasurementSystem]: CarbonUnit };
